import { useEffect, useMemo, useState } from 'react';
import OnelivePageLayout from './OnelivePageLayout';
import OneliveSubPageLayout from './OneliveSubPageLayout';
import { oneliveAssets } from './assets';
import {
  LiveRoom,
  fetchLiveRooms,
  formatAmount,
  normalizeAssetUrl,
} from './api';
import { handleLiveRoomClick } from './liveEntryGuard';
import { useLiveUiMessages } from './useLiveUiMessages';

type RankTier = 1 | 2 | 3;

type PodiumCardProps = {
  room: LiveRoom;
  rank: RankTier;
  onEnter: (room: LiveRoom) => void;
};

type ListRowProps = {
  room: LiveRoom;
  rank: number;
  onEnter: (room: LiveRoom) => void;
};

const PODIUM_ORDER: RankTier[] = [2, 1, 3];

const RANK_LABEL: Record<RankTier, string> = {
  1: '1위',
  2: '2위',
  3: '3위',
};

function resolveAvatar(room: LiveRoom): string {
  return normalizeAssetUrl(room.avatar || room.thumbnail) || oneliveAssets.guestAvatar;
}

function PodiumCard({ room, rank, onEnter }: PodiumCardProps) {
  return (
    <button
      type="button"
      className={`onelive-ranking-podium-card rank-${rank}`}
      onClick={() => onEnter(room)}
    >
      <span className="onelive-ranking-podium-badge">{RANK_LABEL[rank]}</span>
      <span className="onelive-ranking-podium-avatar">
        <img src={resolveAvatar(room)} alt="" loading="lazy" />
      </span>
      <span className="onelive-ranking-podium-meta">
        <strong className="onelive-ranking-podium-name">{room.bj_nickname}</strong>
        <span className="onelive-ranking-podium-title">{room.title}</span>
        <span className="onelive-ranking-podium-viewers">
          <img src={oneliveAssets.rank} alt="" aria-hidden="true" />
          {formatAmount(room.viewer_count ?? 0)}
          <em>명</em>
        </span>
      </span>
    </button>
  );
}

function RankingListRow({ room, rank, onEnter }: ListRowProps) {
  return (
    <li>
      <button type="button" className="onelive-ranking-row" onClick={() => onEnter(room)}>
        <span className="onelive-ranking-row-num">{rank}</span>
        <span className="onelive-ranking-row-avatar">
          <img src={resolveAvatar(room)} alt="" loading="lazy" />
        </span>
        <span className="onelive-ranking-row-body">
          <strong>{room.bj_nickname}</strong>
          <span>{room.title}</span>
        </span>
        <span className="onelive-ranking-row-viewers">{formatAmount(room.viewer_count ?? 0)}명</span>
      </button>
    </li>
  );
}

export default function RankingPage() {
  const liveUiMessages = useLiveUiMessages();
  const [rooms, setRooms] = useState<LiveRoom[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const list = await fetchLiveRooms(50);
      if (mounted) {
        setRooms([...list].sort((a, b) => (b.viewer_count ?? 0) - (a.viewer_count ?? 0)));
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const topThree = useMemo(() => rooms.slice(0, 3), [rooms]);
  const rest = useMemo(() => rooms.slice(3, 20), [rooms]);

  const podiumRooms = useMemo(
    () => PODIUM_ORDER
      .map((rank) => ({ rank, room: topThree[rank - 1] }))
      .filter((item): item is { rank: RankTier; room: LiveRoom } => Boolean(item.room)),
    [topThree],
  );

  const onEnterRoom = (room: LiveRoom) => {
    handleLiveRoomClick(room, liveUiMessages, '/ranking');
  };

  return (
    <OnelivePageLayout pageClassName="onelive-ranking-page">
      <OneliveSubPageLayout title="TOP 랭킹">
          <div className="onelive-ranking-head">
            <p className="onelive-ranking-kicker">MONTHLY ROUND TOP</p>
            <h3 className="onelive-ranking-title">월간 라운 TOP</h3>
            <p className="onelive-ranking-desc">시청자 수 기준 인기 BJ 랭킹</p>
          </div>

          {loading ? (
            <p className="onelive-sub-loading">로딩 중...</p>
          ) : rooms.length === 0 ? (
            <p className="onelive-sub-empty">랭킹 데이터가 없습니다.</p>
          ) : (
            <>
              {podiumRooms.length > 0 ? (
                <div className="onelive-ranking-podium">
                  {podiumRooms.map(({ rank, room }) => (
                    <PodiumCard key={room.uuid} room={room} rank={rank} onEnter={onEnterRoom} />
                  ))}
                </div>
              ) : null}

              {rest.length > 0 ? (
                <section className="onelive-ranking-board">
                  <div className="onelive-ranking-board-head">
                    <h4>4위 ~ {Math.min(rooms.length, 20)}위</h4>
                    <span>실시간 시청자 순</span>
                  </div>
                  <ol className="onelive-ranking-list">
                    {rest.map((room, idx) => (
                      <RankingListRow
                        key={room.uuid}
                        room={room}
                        rank={idx + 4}
                        onEnter={onEnterRoom}
                      />
                    ))}
                  </ol>
                </section>
              ) : null}
            </>
          )}
        </OneliveSubPageLayout>
    </OnelivePageLayout>
  );
}
